// TODO: Fix ESLint issues below
/* eslint-disable prefer-promise-reject-errors */
/* eslint-disable no-restricted-syntax */
import { parseJSONString, isEmpty } from './utils';

/**
 * HTTP options.
 *
 * @typedef {object} HTTPOptions
 * @property {string} [token] Authorization token.
 * @property {boolean} [disableCache] Enable query string timestamp parameter.
 */

/**
 * @typedef {object} HTTPSuccessResponse
 * @property {number} status
 * @property {object|string} data
 * @property {object} requestParams
 */

/**
 * @typedef {object} HTTPErrorResponse
 * @property {number} status
 * @property {text} statusText
 * @property {object} requestParams
 * @property {object} [responseText]
 */

const methods = ['DELETE', 'GET', 'POST'];

const normalizeMethod = (method) => {
  const upperCased = method.toUpperCase();

  return methods.indexOf(upperCased) > -1 ? upperCased : method;
};

const createQueries = (params) => {
  let queryString = '';

  for (const key in params) {
    if (params.hasOwnProperty(key)) {
      queryString += `&${key}=${params[key]}`;
    }
  }

  return queryString.slice(1);
};

export const addParams = (url, params = {}) => {
  if (!isEmpty(params)) {
    const urlWithParams = `${url}${url.includes('?') ? '&' : '?'}${createQueries(params)}`;

    return urlWithParams;
  }

  return url;
};

/**
 * @param {XMLHttpRequest} xhr XMLHttpRequest instance.
 * @param {string} method
 * @param {string} url
 * @param {object} body Post body or query string params.
 * @param {HTTPOptions} options
 * @returns {Promise<HTTPSuccessResponse>}
 */
const request = (xhr, method, url, body, options) => {
  const normalizedMethod = normalizeMethod(method);

  return new Promise((resolve, reject) => {
    xhr.open(normalizedMethod, url, true);

    if (options.token) {
      xhr.setRequestHeader('Authorization', options.token);
    }

    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        // parse JSON and return it, otherwise return response as is
        resolve({
          status: xhr.status,
          data: parseJSONString(xhr.response, xhr.response),
          requestParams: body
        });
      } else {
        reject({
          status: xhr.status,
          statusText: xhr.statusText,
          responseText: parseJSONString(xhr.responseText, {}),
          requestParams: body
        });
      }
    };

    xhr.onerror = () => {
      reject({
        status: xhr.status,
        statusText: xhr.statusText,
        responseText: xhr.responseText,
        requestParams: body
      });
    };

    if (body && normalizedMethod !== 'GET') {
      xhr.setRequestHeader('Content-Type', 'application/json');
      xhr.send(JSON.stringify(body));
    } else {
      xhr.send();
    }
  });
};

/**
 * Create http POST request.
 *
 * @param {string} url
 * @param {object} [params] - Parameters.
 * @param {HTTPOptions} [options = {}] - HTTP options.
 */
export const POST = (url, params, options = {}) => {
  const xhr = new XMLHttpRequest();

  return request(xhr, 'POST', url, params, options);
};

/**
 * Create http DELETE request.
 *
 * @param {string} url
 * @param {object} [params] - Parameters.
 * @param {HTTPOptions} [options = {}] - HTTP options.
 */
export const DELETE = (url, params, options = {}) => {
  const xhr = new XMLHttpRequest();

  const urlWithParams = addParams(url, params);

  return request(xhr, 'DELETE', urlWithParams, {}, options);
};

/**
 * Create http GET request.
 *
 * @param {string} url
 * @param {object} [params] - Query parameters.
 * @param {HTTPOptions} [options = {}] - HTTP options.
 */
export const GET = (url, params, options = {}) => {
  const xhr = new XMLHttpRequest();

  // Add timestamp query parameter to disable caching (actual for IE11)
  if (options.disableCache) {
    params = {
      ...params,
      t: new Date().getTime()
    };
  }

  const urlWithParams = addParams(url, params);

  return request(xhr, 'GET', urlWithParams, params, options);
};

/** @param {HTTPSuccessResponse} response */
export const extractData = (response) => response.data;
